import React, { useState, useEffect, useContext } from "react";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  KeyboardAvoidingView,
  ActivityIndicator} from "react-native";
import AntDesign from "@expo/vector-icons/AntDesign";
import {
  Gender,
  MaritalStatus,
  AccreditedInvestor,
  EmployStatus,
  HouseholdIncome,
  ImmigrationStatus} from "../../../mockData/personalDetailsMockData";
import { SafeAreaView } from "react-native-safe-area-context";
import commonStyles from "../../../styles/commonStyles";
import AsyncStorage from "@react-native-async-storage/async-storage";
import Toast from "react-native-toast-message";
import { ApiService } from "../../../service/ApiService";
import Feather from "@expo/vector-icons/Feather";
import PersonalDetailsView from "./PersonalDetailsView";
import CommonDropdown from "../../../components/Dropdown";
import { IUpdatedUserData, IUserTypes } from "../../../types/IUserTypes";
import PersonalDetailsStyle from "./PersonalDetailsStyle";
import { UserContext } from "../../../contexts/EmulateUserContext";
import AppText from "../../../components/AppText";
import AppTextInput from "../../../components/AppTextInput";

type ProfileProps = {
  navigation: {
    navigate: (screen: string, params?: object) => void;
    goBack: () => void;
  };
};

const PersonalDetails: React.FC<ProfileProps> = ({ navigation }) => {
  const [userData, setUserData] = useState<IUserTypes | null>(null);
  const { emulateUser, setEmulateUser } = useContext(UserContext);
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    employmentStatus: null as string | null,
    email: "",
    phoneNumber: "",
    gender: "" as string | null,
    householdIncome: null as string | null,
    maritalStatus: null as string | null,
    immigrationStatus: null as string | null,
    accreditedInvestor: null as string | null});
  const [isEnabled, setIsEnabled] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fetchUserData = async () => {
    try {
      let apiResponse: string | null;
      // if (emulateUser) {
      apiResponse = await AsyncStorage.getItem("apiResponse");
      // } else {
      //   apiResponse = await AsyncStorage.getItem("user");
      // }

      const userData: IUserTypes = JSON.parse(apiResponse || "{}");
      setUserData(userData);
      if (userData) {
        console.log("the new version .........", userData);
        setFormData({
          firstName: userData.firstName || "",
          middleName: userData.middleName || "",
          lastName: userData.lastName || "",
          email: userData.email || "",
          phoneNumber: userData.phoneNumber || "",
          gender: userData.gender || null,
          householdIncome: userData.householdIncome || null,
          employmentStatus: userData.employmentStatus || null,
          maritalStatus: userData.maritalStatus || null,
          immigrationStatus: userData.immigrationStatus || null,
          accreditedInvestor: userData.accreditedInvestor || null});
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  useEffect(() => {
    console.log("the first of .................");
    fetchUserData();
  }, []);

  const validateAndSubmit = async (): Promise<void> => {
    const newErrors: Record<string, string> = {};
    if (!formData.firstName.trim())
      newErrors["firstName"] = "First Name is required.";
    if (!formData.lastName.trim())
      newErrors["lastName"] = "Last Name is required.";

    if (!formData.gender) newErrors["gender"] = "Gender is required.";
    if (!formData.employmentStatus)
      newErrors["employmentStatus"] = "Employment Status is required.";
    if (!formData.householdIncome)
      newErrors["householdIncome"] = "Household income is required.";
    if (!formData.maritalStatus)
      newErrors["maritalStatus"] = "Marital Status is required.";
    if (!formData.phoneNumber.trim())
      newErrors["phoneNumber"] = "Phone Number is required.";

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      const userData = JSON.parse(
        (await AsyncStorage.getItem("apiResponse")) || "{}"
      );

      setLoading(true);
      const { email, ...filteredFormData } = formData;
      const updatedUserData: IUpdatedUserData = {
        id: userData.id,
        address: userData.address,
        ...filteredFormData,
        bankDetail: userData.bankDetail};

      try {
        const apiUrl = `portal/partner/${userData.id}`;
        const response = await ApiService.put<IUserTypes>(
          apiUrl,
          updatedUserData
        );
        updatedUserData.userRoles = userData.userRoles;
        updatedUserData.email = userData.email;
        updatedUserData.partnerSpouse = userData.partnerSpouse;
        updatedUserData.pendingSpouseRequest = userData.pendingSpouseRequest;

        if (emulateUser) {
          await AsyncStorage.setItem(
            "apiResponse",
            JSON.stringify(updatedUserData)
          );
        } else {
          await AsyncStorage.setItem(
            "apiResponse",
            JSON.stringify(updatedUserData)
          );
          await AsyncStorage.setItem("user", JSON.stringify(response.data));
        }

        Toast.show({
          type: "success",
          position: "top",
          text1: "Data updated successfully!",
          visibilityTime: 4000});

        setLoading(false);
        setUserData(response.data);
        onEdit();
      } catch (error) {
        console.error("Error updating user data:", error);
        Toast.show({
          type: "error",
          position: "top",
          text1: "Failed to update data. Please try again.",
          visibilityTime: 4000});
        setLoading(false);
        setIsEnabled(false);
        setIsEditable(false);
      }
    }
  };

  const onEdit = () => {
    setIsEditable(!isEditable);
    setIsEnabled(!isEnabled);
  };

  const handleInputChange = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  return (
    <>
      <SafeAreaView
        edges={["bottom", "left", "right"]}
        style={[PersonalDetailsStyle.container, commonStyles.screenBottomGap]}
      >
        <View style={[PersonalDetailsStyle.subHeaderContainer]}>
          <TouchableOpacity
            testID="back-button"
            onPress={navigation.goBack}
            style={PersonalDetailsStyle.subHeaderContainer}
          >
            <AntDesign name="left" size={24} color="black" />
            <AppText testID="personal_details" style={commonStyles.subHeader}>Personal Details</AppText>
          </TouchableOpacity>
          <View style={PersonalDetailsStyle.switchContainer}>
            <TouchableOpacity testID="edit-button" onPress={onEdit}>
              <Feather
                name="edit"
                size={24}
                color="black"
                style={commonStyles.mr8}
              />
            </TouchableOpacity>
          </View>
        </View>
        {!isEditable && userData ? (
          <PersonalDetailsView userData={userData} />
        ) : (
          <KeyboardAvoidingView style={{ flex: 1 }} behavior="padding">
            <ScrollView
              contentContainerStyle={[PersonalDetailsStyle.scrollContainer]}
              keyboardShouldPersistTaps="handled"
            >
              <AppText testID="first-name" style={PersonalDetailsStyle.label} >
                First Name <AppText testID = "firstname-asterisk" style={{ color: "black" }}>*</AppText>
              </AppText>
              <AppTextInput
                style={[
                  PersonalDetailsStyle.input,
                  !isEditable && PersonalDetailsStyle.disabledInput,
                  errors["firstName"] && PersonalDetailsStyle.errorBorder,
                ]}
                placeholder="Enter First Name"
                testID="first-name-input"
                placeholderTextColor="#979797"
                value={formData.firstName}
                editable={isEditable}
                onChangeText={(value) => handleInputChange("firstName", value)}
              />
              {errors["firstName"] && (
                <AppText style={PersonalDetailsStyle.errorText} testID="first-name-error">
                  {errors["firstName"]}
                </AppText>
              )}
              <AppText testID="middle-name" style={PersonalDetailsStyle.label}>Middle Name</AppText>
              <AppTextInput
                testID="name-input1"
                style={[
                  PersonalDetailsStyle.input,
                  !isEditable && PersonalDetailsStyle.disabledInput,
                ]}
                placeholder="Enter Middle Name "
                placeholderTextColor="#979797"
                value={formData.middleName}
                editable={isEditable}
                onChangeText={(value) => handleInputChange("middleName", value)}
              />

              <AppText style={PersonalDetailsStyle.label} testID="last-name">
                Last Name <AppText testID = "lastname-asterisk" style={{ color: "black" }}>*</AppText>
              </AppText>
              <AppTextInput
                testID="last-name-input"
                style={[
                  PersonalDetailsStyle.input,
                  !isEditable && PersonalDetailsStyle.disabledInput,
                  errors["lastName"] && PersonalDetailsStyle.errorBorder,
                ]}
                placeholder="Enter Last Name "
                placeholderTextColor="#979797"
                value={formData.lastName}
                editable={isEditable}
                onChangeText={(value) => handleInputChange("lastName", value)}
              />
              {errors["lastName"] && (
                <AppText style={PersonalDetailsStyle.errorText} testID="last-name-error">
                  {errors["lastName"]}
                </AppText>
              )}
              <AppText testID="email" style={PersonalDetailsStyle.label}>Email</AppText>
              <AppTextInput
                testID="email-input"
                style={[
                  PersonalDetailsStyle.input,
                  PersonalDetailsStyle.disabledInput,
                ]}
                placeholder="Enter Email"
                placeholderTextColor="#979797"
                value={formData.email}
                editable={false}
              />

              <CommonDropdown
                testID="gender"
                title="Gender"
                isRequired={true}
                data={Gender}
                value={formData.gender}
                onChange={(item) => handleInputChange("gender", item.value)}
                placeholder="Select gender"
                isEditable={isEditable}
                error={errors["gender"]}
              />

              <CommonDropdown
                testID="employment-status"
                title="Employment Status"
                isRequired={true}
                data={EmployStatus}
                value={formData.employmentStatus}
                onChange={(item) =>
                  handleInputChange("employmentStatus", item.value)
                }
                placeholder="Select employment status"
                isEditable={isEditable}
                error={errors["employmentStatus"]}
              />

              <CommonDropdown
                testID="household-income"
                title="Household Income"
                isRequired={true}
                data={HouseholdIncome}
                value={formData.householdIncome}
                onChange={(item) =>
                  handleInputChange("householdIncome", item.value)
                }
                placeholder="Select household income"
                isEditable={isEditable}
                error={errors["householdIncome"]}
              />

              <CommonDropdown
                testID="marital-status"
                title="Marital Status"
                isRequired={true}
                data={MaritalStatus}
                value={formData.maritalStatus}
                onChange={(item) =>
                  handleInputChange("maritalStatus", item.value)
                }
                placeholder="Select marital status"
                isEditable={isEditable}
                error={errors["maritalStatus"]}
              />

              <AppText testID="phone-number" style={PersonalDetailsStyle.label}>
                Phone Number<AppText testID = "phonenumber-asterisk" style={{ color: "black" }}>*</AppText>
              </AppText>
              <AppTextInput
                testID="phone-number-input"
                style={[
                  PersonalDetailsStyle.input,
                  !isEditable && PersonalDetailsStyle.disabledInput,
                  errors["phoneNumber"]
                    ? PersonalDetailsStyle.errorBorder
                    : null,
                ]}
                value={formData.phoneNumber}
                onChangeText={(value) =>
                  handleInputChange("phoneNumber", value)
                }
                placeholder="Enter phone number"
                placeholderTextColor="#979797"
                keyboardType="phone-pad"
              />
              {errors["phoneNumber"] && (
                <AppText style={PersonalDetailsStyle.errorText} testID="phone-number-error">
                  {errors["phoneNumber"]}
                </AppText>
              )}

              <CommonDropdown
                testID="accredited-investor"
                title="Accredited Investor"
                isRequired={false}
                data={AccreditedInvestor}
                value={formData.accreditedInvestor}
                onChange={(item) =>
                  handleInputChange("accreditedInvestor", item.value)
                }
                placeholder="Select accredited investor status"
                isEditable={isEditable}
              />

              <CommonDropdown
                testID="immigration-status"
                title="Immigration Status"
                isRequired={false}
                data={ImmigrationStatus}
                value={formData.immigrationStatus}
                onChange={(item) =>
                  handleInputChange("immigrationStatus", item.value)
                }
                placeholder="Select immigration status"
                isEditable={isEditable}
              />

              {isEnabled && (
                <TouchableOpacity
                  testID="Update"
                  style={PersonalDetailsStyle.button}
                  onPress={validateAndSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : (
                    <AppText style={PersonalDetailsStyle.buttonText}>Update</AppText>
                  )}
                </TouchableOpacity>
              )}
            </ScrollView>
          </KeyboardAvoidingView>
        )}
      </SafeAreaView>
    </>
  );
};

export default PersonalDetails;
